import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ColunaBoard from './colunaBoard';
import { IEsteira } from '../../interfaces/IEsteira';
import { useAuth } from '../../contexts/AuthProvider';
import { useCallback, useEffect, useState } from 'react';
import { IRelatorio } from '../../interfaces/IRelatorio';
import RelatoriosService from '../../services/RelatoriosService';
import Swal from 'sweetalert2';
import ModalCard from './modal-card';
import ModalExtratoBancario from '../andamentoContas/modal-extrato-bancario';
import { IArquivoCardEsteira } from '../../interfaces/IArquivoCardEsteira';
import ArquivoCardEsteiraService from '../../services/ArquivoCardEsteiraService';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

const Board = ({ esteira, empresaID, connection }: { esteira: IEsteira, empresaID: number, connection?: signalR.HubConnection }) => {

    const { usuario } = useAuth();
    const { t } = useTranslation();
    const [carregandoFiltro, setCarregandoFiltro] = useState<boolean>(false);
    const [filtro, setFiltro] = useState<string>('');

    const [dados, setDados] = useState<IRelatorio[]>([]);
    const [mensagemSistema, setMensagemSistema] = useState<any[]>([]);
    const [intervalo, setIntervalo] = useState<any>(null);

    const [movimentarDe, setMovimentarDe] = useState<string>('');
    const [movimentarPara, setMovimentarPara] = useState<string>('');
    const [loadingMovimentando, setLoadingMovimentando] = useState<boolean>(false);
    const [conciliando, setConciliando] = useState<boolean>(false);
    const [modalExtratoBancario, setModalExtratoBancario] = useState<{ show: boolean; cartaoId: number; cartaoCodigo: string }>({
        show: false,
        cartaoId: 0,
        cartaoCodigo: ''
    });
    const [modal, setShowModal] = useState<any>({
        show: false,
        data: null,
        reprocessar: null
    });

    const showModal = useCallback((codigo, reprocessar) => {
        const acc = dados.find((item) => item.contaEmpresa === codigo);

        setShowModal({
            show: true,
            data: acc,
            updateDescription: (description: string) => {
                setDados((old: IRelatorio[]) => {
                    const value = old || [];
                    const newReference = [...value];
                    const findIndex = newReference.findIndex(f => f.contaEmpresa === acc?.contaEmpresa);
                    if (findIndex >= 0) {
                        newReference[findIndex].descricao = description;
                    }

                    return newReference;
                });

                RelatoriosService.atualizarDescricaoConta(esteira.id, acc?.contaEmpresa || "", description);
            },
            reprocessar: reprocessar ? (anexos: IArquivoCardEsteira[]) => {
                reprocessar(anexos);
                setShowModal({
                    show: false,
                    data: null,
                    reprocessar: null
                });
            } : null
        });
    }, [dados, esteira]);

    const loadData = useCallback((codigo, status) => {
        setDados((old: IRelatorio[]) => {
            if (old) {
                const copy = [...old];
                const index = old?.findIndex((item) => item.contaEmpresa === codigo);
                if (index >= 0) {
                    copy[index].status = status;
                }

                return copy;
            }

            return old;
        })
    }, []);

    const filtroTexto = useCallback((item: IRelatorio) => {
        if (filtro) {
            return `${item.contaEmpresa}-${item.descricaoEmpresa}-${item.contaReferencial}`.toLowerCase().includes(filtro.toLowerCase()) ? item : null;
        }

        return item;
    }, [filtro]);

    const aggragateData = useCallback((newData: IRelatorio[]) => {
        setDados((old) => {
            const cp = [...old];
            const codeExist = cp.map(m => m.contaEmpresa);
            const diff = newData.filter(f => !codeExist.includes(f.contaEmpresa));
            const newer = cp.concat(diff);
            return newer;
        })
    }, [dados]);

    const onSearch = useCallback(() => {

        if (!filtro) {
            Swal.fire({
                text: 'Por favor preencha o filtro.',
                timer: 10000,
                icon: 'error',
                showConfirmButton: false
            });
            return;
        }

        setCarregandoFiltro(true);
        RelatoriosService.buscarCartaoPorFiltro(esteira.id, filtro, true, (resp) => {
            setCarregandoFiltro(false);
            aggragateData(resp);
        })
    }, [filtro, esteira, aggragateData]);

    const movimentarGrupo = useCallback(async () => {
        setLoadingMovimentando(true);

        if (!movimentarDe || !movimentarPara) {
            Swal.fire({
                text: 'Por favor selecione a origem e o destino para enviar os cartões.',
                timer: 10000,
                icon: 'error',
                showConfirmButton: false
            });

            setLoadingMovimentando(false);
            return;
        }

        await RelatoriosService.movimentarStatusConstancia(esteira.id, movimentarDe, movimentarPara);
        setLoadingMovimentando(false);
    }, [movimentarDe, movimentarPara, esteira]);

    const arrastar = useCallback((codigo, status, porcentagem) => {
        setDados((old) => {
            if (old) {
                const copy = [...old];
                const index = old?.findIndex((item) => item.contaEmpresa === codigo);
                if (index >= 0) {
                    copy[index].status = status;
                    if (porcentagem) {
                        copy[index].porcentagemConciliado = porcentagem
                    }
                } else {
                    RelatoriosService.buscarCartao(esteira.id, codigo, (resp) => {
                        if (resp) {
                            copy.push(resp);
                        }
                    });
                }

                return copy;
            }

            return old;
        });
    }, [esteira]);

    const loadDataReport = useCallback((offset: number, status: string, setLoading, setHasMore) => {
        RelatoriosService.buscarCartoes(esteira.id, status, offset, true, (data) => {
            if (!data || data.length == 0) {
                setHasMore(false);
            }
            else {
                aggragateData(data);
            }

            setLoading(false);
        });
    }, [esteira, aggragateData]);

    useEffect(() => {
        if (connection) {
            connection.on("ContaProcessou", (args) => {
                const data = JSON.parse(args);
                if (data.EsteiraID === esteira.id) {
                    arrastar(data.Conta, data.status, data.Porcentagem);
                }
            });
        }
    }, [esteira, connection, arrastar]);

    useEffect(() => {
        if (esteira && !intervalo) {
            ArquivoCardEsteiraService.buscarNotificacoes(esteira.id).then((resp) => setMensagemSistema(resp));
            const i = setInterval(() => {
                ArquivoCardEsteiraService.buscarNotificacoes(esteira.id).then((resp) => setMensagemSistema(resp));
            }, 600000);

            setIntervalo(i);
        }

        return () => {
            clearInterval(intervalo);
        }
    }, [esteira, intervalo]);

    return (
        <div className='row'>
            <div className='col-md-12 mt-2'>
                <div className='row'>
                    <div className='col-md-3 form-group'>
                        <label>{t("De")}:</label>
                        <select disabled={loadingMovimentando} className='form-control' value={movimentarDe} onChange={(e) => setMovimentarDe(e.target.value)}>
                            <option value=''>{t('Selecione uma opção')}</option>
                            <option value='Arquivado'>{t('Arquivado')}</option>
                            <option value='Pendentes'>{t('Pendentes')}</option>
                            <option value='Em processamento'>{t('Conciliação Automática')}</option>
                            <option value='Conciliado Parcial'>{t('Análise Parcial')}</option>
                            <option value='Conciliacao'>{t('Conciliação Inteligente')}</option>
                            <option value='Em Conferencia'>{t('Em Conferência')}</option>
                            <option value='Aguardando Identificacao'>{t('Aguardando Identificação')}</option>
                        </select>
                    </div>
                    <div className='col-md-3 form-group'>
                        <label>{t("Para")}: </label>
                        <select disabled={loadingMovimentando} className='form-control' value={movimentarPara} onChange={(e) => setMovimentarPara(e.target.value)}>

                            <option value=''>{t('Selecione uma opção')}</option>
                            <option value='Arquivado'>{t("Arquivado")}</option>
                            <option value='Pendentes'>{t('Pendentes')}</option>
                            <option value='Em processamento'>{t('Conciliação Automática')}</option>
                            <option value='Conciliado Parcial'>{t('Análise Parcial')}</option>
                            <option value='Conciliacao'>{t('Conciliação Inteligente')}</option>
                            <option value='Em Conferencia'>{t('Em Conferência')}</option>
                            <option value='Aguardando Identificacao'>{t('Aguardando Identificação')}</option>
                            <option value='Concluida'>{t('Concluído')}</option>
                        </select>
                    </div>
                    <div className='col-md-3 mt-6'>
                        <button disabled={loadingMovimentando} className='btn btn-success' onClick={movimentarGrupo}>{t("Movimentar")}</button>
                    </div>
                </div>
            </div>


            <div className='col-md-12 mt-2'>
                <div className='row'>
                    <div className='col-md-6 form-group'>
                        <label>{t("Filtro")}:</label>
                        <input disabled={carregandoFiltro} type='text' placeholder={t('Digite aqui um filtro...')} value={filtro} onChange={(e) => setFiltro(e.target.value)} className='form-control' />
                    </div>
                    <div className='col-md-4'>
                        <button disabled={carregandoFiltro} className='btn btn-success mt-6' onClick={() => onSearch()}>{t("Buscar fora da tela")}</button>
                    </div>
                </div>
            </div>

            <ModalCard
                mostrarContaReferencial
                esteira={esteira.id}
                empresa={empresaID}
                dataI={esteira?.periodoInicio || new Date()}
                dataF={esteira?.periodoFim || new Date()}
                contas={dados}
                updateAll={loadData}
                mensagemSistema={mensagemSistema}
                showModal={showModal}
                toggleModal={() => {
                    setShowModal({
                        show: false,
                        data: null,
                        reprocessar: null
                    });
                }} updateDescription={modal.updateDescription} show={modal.show} acc={modal.data} reprocessar={modal.reprocessar} conciliacaoInteligente={modal.conciliacaoInteligente} processou conferencia={modal?.data?.status === 'Em Conferencia'} onConcluidoClick={() => {

                    RelatoriosService.updateStatusConta(modal.data.contaEmpresa, esteira.id, 'Concluida').then(() => {
                        loadData(modal.data.contaEmpresa, 'Concluida');
                        setShowModal({
                            show: false,
                            data: null,
                            reprocessar: null
                        });
                    });
                }} />

            <ModalExtratoBancario
                show={modalExtratoBancario.show}
                esteiraId={esteira.id}
                cartaoId={modalExtratoBancario.cartaoId}
                onHide={() => setModalExtratoBancario({ show: false, cartaoId: 0, cartaoCodigo: '' })}
                onSuccess={() => arrastar(modalExtratoBancario.cartaoCodigo, 'Em processamento', null)}
            />

            <div className='col-md-12 mt-2 scrollsize'>
                <DndProvider backend={HTML5Backend}>
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Arquivado' || movimentarPara === 'Arquivado')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Arquivado').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Rascunho")} status="Arquivado" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Pendentes' || movimentarPara === 'Pendentes')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Pendentes').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Pendentes")} status="Pendentes" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Em processamento' || movimentarPara === 'Em processamento')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Em processamento').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Conciliação Automática")} status="Em processamento" onRequestExtratoBancario={(item: any) => setModalExtratoBancario({ show: true, cartaoId: item.id, cartaoCodigo: item.codigo })} />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Conciliado Parcial' || movimentarPara === 'Conciliado Parcial')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Conciliado Parcial').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Análise Parcial")} status="Conciliado Parcial" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Conciliacao' || movimentarPara === 'Conciliacao')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Conciliacao').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Conciliação Inteligente")} status="Conciliacao" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Em Conferencia' || movimentarPara === 'Em Conferencia')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Em Conferencia').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Em Conferência")} status="Em Conferencia" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Aguardando Identificacao' || movimentarPara === 'Aguardando Identificacao')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Aguardando Identificacao').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Aguardando Identificação")} status="Aguardando Identificacao" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Concluida' || movimentarPara === 'Concluida')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Concluida').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Concluído")} status="Concluida" />
                </DndProvider>
            </div>
        </div>
    )
}

export default Board;